
// src/pages/EditServiceModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import AddCategoryModal from "../AddSubcategoryModal/AddSubcategoryModal";
import AddSubcategoryModal from "../AddSubcategoryModal/AddSubcategoryModal"

const ADD_CAT_SENTINEL = "__ADD_NEW_CATEGORY__";
const ADD_SUBCAT_SENTINEL = "__ADD_NEW_SUBCATEGORY__";

/**
 * Props:
 * - show: boolean
 * - onClose: () => void
 * - onSave: async (updates: Partial<Service>) => void
 * - service: { id, name, slug, description, icon, price, isActive, category, subcategory }
 * - categories: Array<{ id, key, label, active }>
 * - subcategories: Array<{ id, key, label, categoryKey, active }>
 */
const EditServiceModal = ({
    show,
    onClose,
    onSave,
    service,
    categories = [],
    subcategories = [],
}) => {
    /* ---------- Derivations ---------- */
    const activeCats = useMemo(
        () => categories.filter((c) => c.active),
        [categories]
    );

    const subcatsByCat = useMemo(() => {
        const map = new Map();
        subcategories.forEach((s) => {
            const arr = map.get(s.categoryKey) || [];
            arr.push(s);
            map.set(s.categoryKey, arr);
        });
        // sort each bucket by label for consistent UI
        for (const [k, v] of map) {
            v.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
            map.set(k, v);
        }
        return map;
    }, [subcategories]);

    /* ---------- Local state ---------- */
    const [form, setForm] = useState({
        category: "",
        subcategory: "",
        title: "",
        type: "",
        desc: "",
        icon: "bi-tools",
        price: "",
        active: true,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // inline modals for adding new category/subcategory
    const [showAddCat, setShowAddCat] = useState(false);
    const [showAddSubcat, setShowAddSubcat] = useState(false);

    /* ---------- Initialize form when opened ---------- */
    useEffect(() => {
        if (!show || !service) return;

        setForm({
            category: service.category || activeCats[0]?.key || "",
            subcategory: service.subcategory || "",
            title: service.name || "",
            type: service.slug || "",
            desc: service.description || "",
            icon: service.icon || "bi-tools",
            price: service.price ?? "",
            active: !!service.isActive,
        });
        setError(null);
    }, [show, service, activeCats]);

    /* ---------- Derived options based on selected category ---------- */
    const subcatsForSelected = useMemo(() => {
        if (!form.category) return [];
        const bucket = subcatsByCat.get(form.category) || [];
        return bucket.filter((s) => s.active);
    }, [form.category, subcatsByCat]);

    /* ---------- Handlers ---------- */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === "category") {
            if (value === ADD_CAT_SENTINEL) {
                setShowAddCat(true);
                return;
            }
            // reset subcategory if category changes
            setForm((f) => ({ ...f, category: value, subcategory: "" }));
            return;
        }

        if (name === "subcategory") {
            if (value === ADD_SUBCAT_SENTINEL) {
                setShowAddSubcat(true);
                return;
            }
        }

        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const onCategoryCreated = (createdKey) => {
        // After adding a category, select it and clear subcategory
        setForm((f) => ({ ...f, category: createdKey, subcategory: "" }));
    };

    const onSubcategoryCreated = (subKey, catKey) => {
        // Preselect both parent category and the new subcategory
        setForm((f) => ({ ...f, category: catKey, subcategory: subKey }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!form.category || !form.title) {
            setError(new Error("Category and Title are required."));
            return;
        }

        try {
            setSaving(true);
            await onSave?.({
                // normalize keys to match your service schema
                category: form.category,
                subcategory: form.subcategory || undefined,
                title: form.title,
                slug: form.type || undefined,
                description: form.desc || "",
                icon: form.icon || "bi-tools",
                price: form.price,
                isActive: !!form.active,
            });
            onClose?.();
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    /* ---------- Early return AFTER hooks ---------- */
    if (!show || !service) return null;

    return (
        <div className="modal d-block" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content shadow">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Service</h5>
                        <button className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>

                    <form onSubmit={onSubmit}>
                        <div className="modal-body">
                            {error && <div className="alert alert-danger">{error.message}</div>}

                            <div className="row g-3">
                                {/* Category */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label" htmlFor="edit-category">Category</label>
                                    <select
                                        id="edit-category"
                                        name="category"
                                        className="form-select"
                                        value={form.category}
                                        onChange={handleChange}
                                        required
                                    >
                                        {activeCats.length === 0 ? (
                                            <>
                                                <option value="">— No categories —</option>
                                                <option value={ADD_CAT_SENTINEL}>➕ Add new category…</option>
                                            </>
                                        ) : (
                                            <>
                                                {activeCats.map((c) => (
                                                    <option key={c.id} value={c.key}>{c.label}</option>
                                                ))}
                                                <option value={ADD_CAT_SENTINEL}>➕ Add new category…</option>
                                            </>
                                        )}
                                    </select>
                                </div>

                                {/* Subcategory */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label" htmlFor="edit-subcategory">Subcategory</label>
                                    <select
                                        id="edit-subcategory"
                                        name="subcategory"
                                        className="form-select"
                                        value={form.subcategory}
                                        onChange={handleChange}
                                        disabled={!form.category}
                                    >
                                        {!form.category ? (
                                            <option value="">— Select category first —</option>
                                        ) : subcatsForSelected.length === 0 ? (
                                            <>
                                                <option value="">— No subcategories —</option>
                                                <option value={ADD_SUBCAT_SENTINEL}>➕ Add new subcategory…</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="">(None)</option>
                                                {subcatsForSelected.map((s) => (
                                                    <option key={s.id} value={s.key}>{s.label}</option>
                                                ))}
                                                <option value={ADD_SUBCAT_SENTINEL}>➕ Add new subcategory…</option>
                                            </>
                                        )}
                                    </select>
                                </div>

                                {/* Slug */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label" htmlFor="edit-type">Service Slug (optional)</label>
                                    <input
                                        id="edit-type"
                                        name="type"
                                        className="form-control"
                                        placeholder="e.g., maid-basic"
                                        value={form.type}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Title */}
                                <div className="col-12">
                                    <label className="form-label" htmlFor="edit-title">Title</label>
                                    <input
                                        id="edit-title"
                                        name="title"
                                        className="form-control"
                                        value={form.title}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                {/* Description */}
                                <div className="col-12">
                                    <label className="form-label" htmlFor="edit-desc">Description</label>
                                    <textarea
                                        id="edit-desc"
                                        name="desc"
                                        className="form-control"
                                        rows={3}
                                        value={form.desc}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Icon */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label" htmlFor="edit-icon">Icon Class</label>
                                    <input
                                        id="edit-icon"
                                        name="icon"
                                        className="form-control"
                                        placeholder="e.g., bi-person-lines-fill"
                                        value={form.icon}
                                        onChange={handleChange}
                                    />
                                    <small className="text-muted">
                                        Use Bootstrap Icons + <code>text-primary-label</code> in UI.
                                    </small>
                                </div>

                                {/* Price */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label" htmlFor="edit-price">Starting Price (₹)</label>
                                    <input
                                        id="edit-price"
                                        name="price"
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="form-control"
                                        value={form.price}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                {/* Active */}
                                <div className="col-12 col-md-4 d-flex align-items-center">
                                    <div className="form-check mt-4">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="edit-active"
                                            name="active"
                                            checked={form.active}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label" htmlFor="edit-active">Active</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-outline-secondary" type="button" onClick={onClose}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" type="submit" disabled={saving}>
                                {saving ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Backdrop */}
            {/* <div className="modal-backdrop fade show" /> */}

            {/* Inline modals for adding category/subcategory */}
            <AddCategoryModal
                show={showAddCat}
                onClose={() => setShowAddCat(false)}
                onCreated={onCategoryCreated}
            />
            <AddSubcategoryModal
                show={showAddSubcat}
                onClose={() => setShowAddSubcat(false)}
                onCreated={onSubcategoryCreated}
                categories={categories}
                initialCategoryKey={form.category}
            />
        </div>
    );
};

export default EditServiceModal;